import React, { useEffect, useMemo, useState } from 'react';
import { ArrowLeft, Sprout, Upload } from 'lucide-react';
import { supabase } from '../../supabase';
import { useEmpresaId } from '../../context/EmpresaContext';
import { Parcela, EmpBloque } from '../../types/empacadora';
import { formatNumber } from '../../utils/erpLocale';
import { invocarMotorPythonBatchArchivoV3, type MotorPythonBatchResponse } from './deteccionService';

export function ConteoPlantasV3TestPage() {
  const empresaId = useEmpresaId();
  const [parcelas, setParcelas] = useState<Parcela[]>([]);
  const [bloques, setBloques] = useState<EmpBloque[]>([]);
  const [selectedParcelaId, setSelectedParcelaId] = useState('');
  const [selectedBloqueIds, setSelectedBloqueIds] = useState<string[]>([]);
  const [rasterFile, setRasterFile] = useState<File | null>(null);
  const [params, setParams] = useState({ minArea: 20, maxArea: 800 });
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState<MotorPythonBatchResponse | null>(null);

  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      const [{ data: parcelasData, error: parcelasError }, { data: bloquesData, error: bloquesError }] = await Promise.all([
        supabase
          .from('emp_parcelas')
          .select('id, empresa_id, proveedor_id, codigo, nombre, hectareas, geojson, activo, tipo_finca, area_ha_perimetro, area_ha_sembrada, created_at')
          .eq('empresa_id', empresaId)
          .eq('activo', true)
          .order('nombre'),
        supabase
          .from('emp_bloques')
          .select('*')
          .eq('empresa_id', empresaId)
          .eq('activo', true)
          .eq('tipo', 'siembra')
          .order('num'),
      ]);
      if (!active) return;
      if (parcelasError || bloquesError) {
        setError(parcelasError?.message || bloquesError?.message || 'No se pudo cargar datos');
      } else {
        const p = (parcelasData || []) as Parcela[];
        const b = (bloquesData || []) as EmpBloque[];
        setParcelas(p);
        setBloques(b);
        const first = p[0]?.id || '';
        setSelectedParcelaId(first);
      }
      setLoading(false);
    })();
    return () => {
      active = false;
    };
  }, [empresaId]);

  const bloquesParcela = useMemo(
    () => bloques.filter((b) => b.parcela_id === selectedParcelaId && b.geojson),
    [bloques, selectedParcelaId]
  );

  useEffect(() => {
    setSelectedBloqueIds(bloquesParcela.map((b) => b.id));
  }, [bloquesParcela]);

  const selectedParcela = parcelas.find((p) => p.id === selectedParcelaId) || null;

  async function runV3() {
    if (!selectedParcela) {
      setError('Seleccione una parcela');
      return;
    }
    if (!rasterFile) {
      setError('Seleccione un archivo RGB/GeoTIFF');
      return;
    }
    const bloquesPayload = bloquesParcela
      .filter((b) => selectedBloqueIds.includes(b.id))
      .map((b) => ({
        bloque_id: b.id,
        bloque_num: b.num,
        area_ha: b.area_ha,
        geojson: b.geojson,
      }));
    if (bloquesPayload.length === 0) {
      setError('Seleccione al menos un bloque');
      return;
    }

    setRunning(true);
    setError('');
    setResult(null);
    try {
      const loteId = selectedParcela.codigo || selectedParcela.nombre || selectedParcela.id;
      const payload = await invocarMotorPythonBatchArchivoV3(loteId, params, bloquesPayload, rasterFile);
      setResult(payload);
    } catch (e: any) {
      setError(e?.message || String(e));
    } finally {
      setRunning(false);
    }
  }

  return (
    <div className="w-full p-6 space-y-5">
      <div className="rounded-2xl border border-line bg-surface-raised p-5">
        <div className="flex items-center gap-3">
          <ArrowLeft size={16} />
          <div>
            <h2 className="text-xl font-semibold text-ink">Conteo de Plantas V3 (Prueba)</h2>
            <p className="text-sm ink-2">Prueba del algoritmo nuevo basado en conte_0.py sin afectar V2.</p>
          </div>
        </div>
      </div>

      <div className="rounded-2xl border border-line bg-surface-raised p-5 space-y-4">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <div>
            <label className="text-xs ink-2">Parcela</label>
            <select
              className="mt-1 w-full rounded-lg border border-line bg-surface px-3 py-2 text-sm text-ink"
              value={selectedParcelaId}
              onChange={(e) => setSelectedParcelaId(e.target.value)}
            >
              {parcelas.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.codigo || '-'} - {p.nombre}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="text-xs ink-2">Min area (px)</label>
            <input
              type="number"
              className="mt-1 w-full rounded-lg border border-line bg-surface px-3 py-2 text-sm text-ink"
              value={params.minArea}
              onChange={(e) => setParams((p) => ({ ...p, minArea: Math.max(1, Number(e.target.value) || 1) }))}
            />
          </div>
          <div>
            <label className="text-xs ink-2">Max area (px)</label>
            <input
              type="number"
              className="mt-1 w-full rounded-lg border border-line bg-surface px-3 py-2 text-sm text-ink"
              value={params.maxArea}
              onChange={(e) => setParams((p) => ({ ...p, maxArea: Math.max(1, Number(e.target.value) || 1) }))}
            />
          </div>
        </div>

        <div className="rounded-xl border border-line bg-surface p-3">
          <div className="mb-2 flex items-center justify-between">
            <p className="text-sm text-ink">Bloques de siembra ({bloquesParcela.length})</p>
            <div className="flex gap-2 text-xs">
              <button
                type="button"
                className="rounded border border-line px-2 py-1 ink-2"
                onClick={() => setSelectedBloqueIds(bloquesParcela.map((b) => b.id))}
              >
                Seleccionar todos
              </button>
              <button type="button" className="rounded border border-line px-2 py-1 ink-2" onClick={() => setSelectedBloqueIds([])}>
                Limpiar
              </button>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
            {bloquesParcela.map((b) => (
              <label key={b.id} className="flex items-center gap-2 rounded border border-line px-2 py-1 text-xs ink-2">
                <input
                  type="checkbox"
                  checked={selectedBloqueIds.includes(b.id)}
                  onChange={(e) =>
                    setSelectedBloqueIds((prev) =>
                      e.target.checked ? (prev.includes(b.id) ? prev : [...prev, b.id]) : prev.filter((id) => id !== b.id)
                    )
                  }
                />
                B{b.num} - {typeof b.area_ha === 'number' ? `${b.area_ha.toFixed(2)} ha` : '-'}
              </label>
            ))}
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <label className="inline-flex cursor-pointer items-center gap-2 rounded-lg border border-line bg-surface px-3 py-2 text-sm text-ink">
            <Upload size={14} />
            {rasterFile ? rasterFile.name : 'Subir RGB/GeoTIFF'}
            <input
              type="file"
              accept=".tif,.tiff,image/tiff"
              className="hidden"
              onChange={(e) => setRasterFile(e.target.files?.[0] || null)}
            />
          </label>
          <button
            type="button"
            onClick={runV3}
            disabled={running || loading}
            className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white disabled:opacity-60"
          >
            <Sprout size={14} />
            {running ? 'Ejecutando V3...' : 'Ejecutar V3'}
          </button>
        </div>

        {error && <div className="rounded-lg border border-red-800/60 bg-red-950/30 px-3 py-2 text-sm text-red-300">{error}</div>}
      </div>

      {result && (
        <div className="rounded-2xl border border-line bg-surface-raised p-5 space-y-3">
          <p className="text-sm ink-2">{result.mensaje || 'Resultado V3'}</p>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="text-left ink-2">
                <tr>
                  <th className="py-2 pr-3">Bloque</th>
                  <th className="py-2 pr-3">Plantas</th>
                  <th className="py-2 pr-3">Hileras</th>
                  <th className="py-2 pr-3">Long. m</th>
                  <th className="py-2 pr-3">Densidad</th>
                  <th className="py-2 pr-3">KPI</th>
                </tr>
              </thead>
              <tbody className="text-ink">
                {(result.bloques || []).map((row, idx) => (
                  <tr key={`${row.bloque_id || idx}`} className="border-t border-line">
                    <td className="py-2 pr-3">B{row.bloque_num || '-'}</td>
                    <td className="py-2 pr-3">{formatNumber(Number(row.conteo || 0))}</td>
                    <td className="py-2 pr-3">{formatNumber(Number((row as any).hileras || 0))}</td>
                    <td className="py-2 pr-3">{Number((row as any).longitud_m || 0).toFixed(1)}</td>
                    <td className="py-2 pr-3">{Number(row.densidad || 0).toFixed(0)} pl/ha</td>
                    <td className="py-2 pr-3">{Number(row.kpi || 0).toFixed(1)}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex flex-wrap gap-4 text-sm">
            {result.resultado_reporte_url && (
              <a href={result.resultado_reporte_url} target="_blank" rel="noreferrer" className="text-emerald-300 underline">
                Abrir reporte V3
              </a>
            )}
            {result.resultado_json_url && (
              <a href={result.resultado_json_url} target="_blank" rel="noreferrer" className="text-sky-300 underline">
                Abrir GeoJSON V3
              </a>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default ConteoPlantasV3TestPage;
